import {
  type AuthenticatedTransport,
  requestJson,
  requireStatus,
  submitAccountEmailRequest,
} from "@groundspark/browser-auth";
import {
  identifier,
  integer,
  list,
  object,
  parseBranch,
  parseElectorRecord,
  parseHouse,
  parseInvitation,
  parseIssuedInvitation,
  parseIssuedSession,
  parseMembershipPass,
  parseMembershipVerdict,
  parseNotification,
  parsePeerLocation,
  parseProjectSummary,
  parseRegister,
  parseRoad,
  parseSession,
  parseStreetRecord,
  unique,
  type Answers,
  type Position,
} from "./domain";

export class CanvassApi {
  public constructor(
    public readonly transport: AuthenticatedTransport,
    private readonly apiOrigin: string,
  ) {}

  private json<T>(
    path: string,
    parse: (value: unknown) => T,
    method = "GET",
    body?: unknown,
    signal?: AbortSignal,
  ): Promise<T> {
    return requestJson(this.transport, path, parse, "canvassing", {
      method,
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      signal,
    });
  }

  public async empty(
    path: string,
    method = "POST",
    body?: unknown,
    expected = 204,
    signal?: AbortSignal,
  ): Promise<void> {
    const response = await this.transport.authenticatedFetch(path, {
      method,
      signal,
      ...(body === undefined
        ? {}
        : { headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) }),
    });
    await requireStatus(response, expected, signal);
  }

  public sessions() {
    return this.json("/v1/canvassing-sessions", (value) =>
      unique(list(value, parseSession), (item) => item.id),
    );
  }
  public projects() {
    return this.json("/v1/canvassing-projects", (value) =>
      unique(list(value, parseProjectSummary), (item) => item.id),
    );
  }
  public branches() {
    return this.json("/v1/branches", (value) =>
      unique(list(value, parseBranch), (item) => item.id),
    );
  }
  public registers() {
    return this.json("/v1/electoral-registers", (value) =>
      unique(list(value, parseRegister), (item) => item.id),
    );
  }
  public progress(signal?: AbortSignal) {
    return this.json(
      "/v1/account/canvass-progress",
      (value) => {
        const data = object(value);
        return {
          elector_count: integer(data.elector_count),
          street_count: integer(data.street_count),
        };
      },
      "GET",
      undefined,
      signal,
    );
  }

  public createProject(name: string, branch: string, register: string | null) {
    return this.json("/v1/canvassing-projects", parseProjectSummary, "POST", {
      name,
      branch_id: identifier(branch),
      electoral_register_id: register,
    });
  }
  public renameProject(id: string, name: string) {
    return this.json(
      `/v1/canvassing-projects/${identifier(id)}`,
      parseProjectSummary,
      "PATCH",
      { name },
    );
  }
  public deleteProject(id: string) {
    return this.empty(`/v1/canvassing-projects/${identifier(id)}`, "DELETE");
  }
  public startSession(project: string) {
    return this.json("/v1/canvassing-sessions", parseIssuedSession, "POST", {
      project_id: identifier(project),
    });
  }
  public joinSession(code: string) {
    return this.json("/v1/canvassing-sessions/join", parseSession, "POST", { join_code: code });
  }
  public regenerateSession(id: string) {
    return this.json(
      `/v1/canvassing-sessions/${identifier(id)}/regenerate-join-code`,
      parseIssuedSession,
      "POST",
    );
  }
  public extendSession(id: string) {
    return this.json(`/v1/canvassing-sessions/${identifier(id)}/extend`, parseSession, "POST");
  }
  public endSession(id: string) {
    return this.empty(`/v1/canvassing-sessions/${identifier(id)}/end`);
  }
  public kickParticipant(session: string, user: string) {
    return this.empty(
      `/v1/canvassing-sessions/${identifier(session)}/participants/${identifier(user)}/remove`,
    );
  }
  public publishLocation(id: string, position: Position, signal?: AbortSignal) {
    return this.json(
      `/v1/canvassing-sessions/${identifier(id)}/location`,
      (value) => unique(list(value, parsePeerLocation), (item) => item.user.id),
      "PUT",
      position,
      signal,
    );
  }
  public clearLocation(id: string) {
    return this.empty(`/v1/canvassing-sessions/${identifier(id)}/location`, "DELETE");
  }

  public nearby(
    project: string,
    position: Position,
    streetProject: boolean,
    signal?: AbortSignal,
  ) {
    return this.json(
      `${this.projectPath(project)}/nearby-roads`,
      (value) => {
        const roads = unique(list(object(value).roads, parseRoad), (item) => item.usrn);
        if (streetProject && roads.some((road) => road.canvass === null))
          throw new TypeError("Missing street completions.");
        return roads.sort(
          (a, b) =>
            a.distance_meters - b.distance_meters ||
            a.name.localeCompare(b.name) ||
            a.usrn - b.usrn,
        );
      },
      "POST",
      position,
      signal,
    );
  }
  public houses(project: string, position: Position, usrn: number, signal?: AbortSignal) {
    return this.json(
      `${this.projectPath(project)}/street-houses`,
      (value) => unique(list(object(value).houses, parseHouse), (item) => item.address_id),
      "POST",
      { ...position, usrn: integer(usrn, 1) },
      signal,
    );
  }
  public elector(project: string, elector: string, signal?: AbortSignal) {
    return this.json(
      this.electorPath(project, elector),
      (value) => {
        const record = parseElectorRecord(value);
        if (record.elector_id !== elector) throw new TypeError("Voter identity mismatch.");
        return record;
      },
      "GET",
      undefined,
      signal,
    );
  }
  public saveElector(
    project: string,
    elector: string,
    answers: Answers & { readonly notes: string },
    signal?: AbortSignal,
  ) {
    return this.json(
      this.electorPath(project, elector),
      (value) => {
        const record = parseElectorRecord(value);
        if (record.elector_id !== elector) throw new TypeError("Voter identity mismatch.");
        return record;
      },
      "PUT",
      answers,
      signal,
    );
  }
  public street(project: string, usrn: number, signal?: AbortSignal) {
    return this.json(
      this.streetPath(project, usrn),
      (value) => {
        const record = parseStreetRecord(value);
        if (record.usrn !== usrn) throw new TypeError("Street identity mismatch.");
        return record;
      },
      "GET",
      undefined,
      signal,
    );
  }
  public saveStreet(
    project: string,
    usrn: number,
    completed: boolean,
    revision: number,
    signal?: AbortSignal,
  ) {
    return this.json(
      this.streetPath(project, usrn),
      (value) => {
        const record = parseStreetRecord(value);
        if (record.usrn !== usrn) throw new TypeError("Street identity mismatch.");
        return record;
      },
      "PUT",
      { completed, expected_revision: revision },
      signal,
    );
  }

  public membershipPass(signal?: AbortSignal) {
    return this.json("/v1/membership/pass", parseMembershipPass, "POST", undefined, signal);
  }
  public verifyMembership(code: string, signal?: AbortSignal) {
    return this.json("/v1/membership/verify", parseMembershipVerdict, "POST", { code }, signal);
  }
  public invite(signal?: AbortSignal) {
    return this.json("/v1/invitations/qr", parseIssuedInvitation, "POST", undefined, signal);
  }
  public unreadCount(signal?: AbortSignal) {
    return this.json(
      "/v1/notifications/unread-count",
      (value) => integer(object(value).unread_count),
      "GET",
      undefined,
      signal,
    );
  }
  public notifications(cursor: string | null, signal?: AbortSignal) {
    const query = new URLSearchParams({ limit: "50" });
    if (cursor !== null) query.set("cursor", identifier(cursor));
    return this.json(
      `/v1/notifications?${query}`,
      (value) => {
        const data = object(value);
        return {
          items: unique(list(data.items, parseNotification), (item) => item.id),
          next_cursor: data.next_cursor === null ? null : identifier(data.next_cursor),
        };
      },
      "GET",
      undefined,
      signal,
    );
  }
  public readNotification(id: string, signal?: AbortSignal) {
    return this.json(
      `/v1/notifications/${identifier(id)}/read`,
      parseNotification,
      "POST",
      undefined,
      signal,
    );
  }
  public changeEmail(email: string, password: string, signal?: AbortSignal) {
    return this.empty(
      "/v1/account/email-change",
      "POST",
      { new_email: email, current_password: password },
      202,
      signal,
    );
  }
  public changePassword(password: string, next: string, signal?: AbortSignal) {
    return this.empty(
      "/v1/account/password",
      "POST",
      { current_password: password, new_password: next },
      204,
      signal,
    );
  }
  public async inspectInvitation(code: string, signal?: AbortSignal) {
    const transport: AuthenticatedTransport = {
      authenticatedFetch: (path, init) =>
        fetch(`${this.apiOrigin}${path}`, {
          ...init,
          credentials: "omit",
          referrerPolicy: "no-referrer",
          cache: "no-store",
          signal: signal
            ? AbortSignal.any([signal, AbortSignal.timeout(20_000)])
            : AbortSignal.timeout(20_000),
        }),
    };
    return requestJson(transport, "/v1/invitations/inspect", parseInvitation, "invitation", {
      method: "POST",
      body: JSON.stringify({ invitation_code: code }),
      signal,
    });
  }
  public submitSignup(body: Readonly<Record<string, string>>) {
    return submitAccountEmailRequest(this.apiOrigin, "/v1/auth/signup", body, 202);
  }
  public confirmEmail(action: "verify" | "change" | "reset", token: string, password: string) {
    return submitAccountEmailRequest(
      this.apiOrigin,
      action === "reset"
        ? "/v1/auth/password-reset/complete"
        : action === "change"
          ? "/v1/auth/confirm-email-change"
          : "/v1/auth/verify-email",
      action === "reset" ? { token, new_password: password } : { token, password },
      204,
    );
  }

  private projectPath(project: string) {
    return `/v1/canvassing-projects/${identifier(project)}/canvass`;
  }
  private electorPath(project: string, elector: string) {
    if (!/^[a-f0-9]{64}$/.test(elector)) throw new TypeError("Invalid voter identity.");
    return `${this.projectPath(project)}/electors/${elector}/result`;
  }
  private streetPath(project: string, usrn: number) {
    return `${this.projectPath(project)}/streets/${integer(usrn, 1)}/result`;
  }
}
